import React from "react";
import { Icon } from "./Icon";

export default function EmptyStateBanner({
  badge,
  title,
  description,
  primaryCta,
  onPrimaryAction,
  secondaryCta,
  onSecondaryAction,
  tertiaryCta,
  onTertiaryAction,
}) {
  // Automatically scrub any non-compliant terms if passed in props
  const scrubText = (txt) => {
    if (!txt) return "";
    return txt
      .replace(/Approved/gi, "Available")
      .replace(/Verified/gi, "Experienced")
      .replace(/Certified/gi, "Experienced")
      .replace(/Guaranteed/gi, "Top Rated");
  };

  const safeTitle = scrubText(title);
  const safeDescription = scrubText(description);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden relative shadow-[0_2px_10px_rgba(15,23,42,0.04)] p-5 flex flex-col md:flex-row items-center justify-between gap-5">
      {/* Left Content Area */}
      <div className="flex-1 max-w-xl">
        {badge && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F1F7E3] text-[#5E8700] text-[9.5px] font-bold mb-3">
            <span className="w-2 h-2 rounded-full bg-[#7BAE00] animate-pulse" />
            {badge}
          </div>
        )}
        <h3 className="text-[14px] font-extrabold text-[#0F172A] leading-snug mb-1.5">
          {safeTitle}
        </h3>
        <p className="text-[10.5px] text-[#64748B] leading-snug mb-5">
          {safeDescription}
        </p>

        {/* Action Buttons Row - 44px, and free to wrap when a language needs two lines. */}
        <div className="flex flex-wrap items-center gap-2">
          {primaryCta && (
            <button
              onClick={onPrimaryAction}
              className="min-h-[44px] px-4 rounded-xl bg-[#7BAE00] hover:bg-[#689300] text-white text-[11.5px] font-bold shadow-[0_4px_12px_rgba(123,174,0,0.25)] transition-all duration-200 flex items-center gap-1.5 active:scale-[0.98]"
            >
              <span>{primaryCta}</span>
              <Icon name="arrowRight" size={14} />
            </button>
          )}
          {secondaryCta && (
            <button
              onClick={onSecondaryAction}
              className="min-h-[44px] px-4 rounded-xl bg-white border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#334155] text-[11.5px] font-bold transition-all duration-200 active:scale-[0.98]"
            >
              {secondaryCta}
            </button>
          )}
          {tertiaryCta && (
            <button
              onClick={onTertiaryAction}
              className="min-h-[44px] px-4 rounded-xl bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#1E293B] text-[11.5px] font-bold transition-all duration-200 active:scale-[0.98]"
            >
              {tertiaryCta}
            </button>
          )}
        </div>
      </div>

      {/* Right Hero Illustration (Founder Approved: White Cab, Professional Driver, Road, Trees, Green Pin) */}
      <div className="w-full md:w-[260px] shrink-0 flex items-center justify-center">
        <svg viewBox="0 0 260 160" className="w-full h-auto max-w-[240px]" fill="none">
          {/* Soft Background Cloud/Landscape */}
          <ellipse cx="130" cy="135" rx="110" ry="20" fill="#F4F9EB" />
          <path d="M40 135 Q70 100 110 135 T190 135" fill="#E6F2D6" opacity="0.6" />
          
          {/* Trees */}
          <circle cx="60" cy="115" r="14" fill="#84CC16" opacity="0.4" />
          <rect x="58" y="125" width="4" height="10" fill="#94A3B8" />
          <circle cx="205" cy="110" r="18" fill="#7BAE00" opacity="0.3" />
          <rect x="203" y="122" width="4" height="13" fill="#94A3B8" />

          {/* Road */}
          <path d="M20 145 Q130 145 240 145" stroke="#CBD5E1" strokeWidth="8" strokeLinecap="round" />
          <path d="M50 145 L80 145 M115 145 L145 145 M180 145 L210 145" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="6 6" />

          {/* White Sramika Cab */}
          <g transform="translate(85, 95)">
            {/* Shadow */}
            <ellipse cx="50" cy="45" rx="45" ry="5" fill="#0F172A" opacity="0.1" />
            {/* Car Body */}
            <path d="M5 32 L15 15 L75 15 L88 32 L92 32 C95 32 96 35 96 38 L96 42 C96 44 94 45 92 45 L5 45 C3 45 2 44 2 42 L2 38 C2 35 3 32 5 32 Z" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="2" />
            {/* Sramika Green Accent Roof/Window Trim */}
            <path d="M18 17 L72 17 L83 31 L11 31 Z" fill="#F4F9EB" stroke="#7BAE00" strokeWidth="1.5" />
            <line x1="45" y1="17" x2="45" y2="31" stroke="#7BAE00" strokeWidth="1.5" />
            {/* Wheels */}
            <circle cx="22" cy="45" r="7" fill="#334155" />
            <circle cx="22" cy="45" r="3" fill="#94A3B8" />
            <circle cx="75" cy="45" r="7" fill="#334155" />
            <circle cx="75" cy="45" r="3" fill="#94A3B8" />
          </g>

          {/* Professional Driver Standing Beside Cab */}
          <g transform="translate(60, 92)">
            {/* Shadow */}
            <ellipse cx="12" cy="50" rx="10" ry="3" fill="#0F172A" opacity="0.15" />
            {/* Legs */}
            <rect x="8" y="32" width="3.5" height="18" rx="1.5" fill="#1E293B" />
            <rect x="13" y="32" width="3.5" height="18" rx="1.5" fill="#1E293B" />
            {/* Torso (White Shirt with Green Tie/Collar) */}
            <rect x="6" y="14" width="13" height="19" rx="3" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="1.5" />
            <path d="M12 14 L13 25 L11 25 Z" fill="#7BAE00" />
            {/* Arms */}
            <rect x="3" y="15" width="3" height="14" rx="1.5" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="1" />
            <rect x="19" y="15" width="3" height="14" rx="1.5" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="1" />
            {/* Head */}
            <circle cx="12.5" cy="8" r="5.5" fill="#FDE047" stroke="#CBD5E1" strokeWidth="1" />
            {/* Driver Cap */}
            <path d="M6 6 Q12.5 2 19 6 L21 6 L21 4 Q12.5 0 4 4 Z" fill="#7BAE00" />
          </g>

          {/* Navigation Pin */}
          <g transform="translate(130, 25)">
            <path d="M15 0 C6.7 0 0 6.7 0 15 C0 26.2 15 42 15 42 C15 42 30 26.2 30 15 C30 6.7 23.3 0 15 0 Z" fill="#7BAE00" />
            <circle cx="15" cy="14" r="6" fill="#FFFFFF" />
          </g>
        </svg>
      </div>
    </div>
  );
}
